'use client';

import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';
import { materializePublicUserIdentity, publicUserAliases, publicUserAliasKey, publicUserEntityKey, publicUserSnapshot, reconcilePublicUserAvatarModeration, upsertPublicUserEntity } from '../internal/public-user-entity.js';
import { createSubscriberQueryFn } from '../internal/subscriber-query-fn.js';
const MISSING_ALIAS = '__missing_public_user_alias__';
export function usePublicUserIdentity(input, extraAliases = [], options) {
  const providedQueryClient = useContext(QueryClientContext);
  const fallbackQueryClient = useRef(null);
  fallbackQueryClient.current ??= new QueryClient();
  // Leaf identity components are also rendered in isolated previews and tests.
  // Give their disabled observers a private client while leaving the embedded
  // snapshot untouched; the app provider remains the canonical production cache.
  const queryClient = providedQueryClient ?? fallbackQueryClient.current;
  const row = toRecord(input);
  const aliases = publicUserAliases(row, extraAliases);
  const primaryAlias = aliases[0] ?? MISSING_ALIAS;
  const aliasKey = publicUserAliasKey(primaryAlias);
  const {
    data: aliasedEntityId
  } = useQuery({
    queryKey: aliasKey,
    queryFn: createSubscriberQueryFn(queryClient, aliasKey),
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY
  }, queryClient);
  const entityId = aliasedEntityId ?? String(aliasKey[2]);
  const entityKey = publicUserEntityKey(entityId);
  const {
    data: entity
  } = useQuery({
    queryKey: entityKey,
    queryFn: createSubscriberQueryFn(queryClient, entityKey),
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY
  }, queryClient);
  const publicSnapshot = JSON.stringify({
    entity: publicUserSnapshot(row),
    aliases
  });
  const moderationUserId = options?.avatarModeration?.userId;
  const moderationAvatarUrl = options?.avatarModeration?.avatarUrl;
  const moderationAvatarNsfw = options?.avatarModeration?.avatarNsfw;
  useEffect(() => {
    if (providedQueryClient === undefined) return;
    const parsed = JSON.parse(publicSnapshot);
    if (parsed.aliases.length === 0) return;
    upsertPublicUserEntity(queryClient, parsed.entity, {
      source: 'hydrate',
      observedAtMs: 0,
      aliases: parsed.aliases
    });
    if (moderationUserId !== undefined && moderationAvatarUrl !== undefined && moderationAvatarNsfw !== undefined) {
      reconcilePublicUserAvatarModeration(queryClient, {
        userId: moderationUserId,
        avatarUrl: moderationAvatarUrl,
        avatarNsfw: moderationAvatarNsfw
      });
    }
  }, [providedQueryClient, queryClient, publicSnapshot, moderationUserId, moderationAvatarUrl, moderationAvatarNsfw]);
  if (input === null || typeof input !== 'object') return input;
  return materializePublicUserIdentity(row, entity);
}
export function isPublicUserIdentityField(field) {
  return Object.hasOwn(publicUserSnapshot({
    [field]: true
  }), field);
}
function toRecord(value) {
  return value !== null && typeof value === 'object' ? value : {};
}